import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useDemoRole } from '../../../../contexts/DemoRoleContext';
import { assembleReport, buildScopeOptions } from '../../utils/reportData';
import { useAuth } from '../../../../contexts/AuthContext';
import { useUserOrganizations } from '../../hooks/useOrganization';
import { useSiteComparisons } from '../../hooks/useSiteComparisons';
import OrgSwitcher from '../../components/OrgSwitcher';
import { exportReportPDF, exportReportCSV, exportReportDoc } from '../../utils/reportExport';

const REPORT_TEMPLATES = [
  {
    id: 'comparison',
    icon: TableChartIcon,
    title: 'Comparison Report',
    description: 'REPS scores by quadrant with warning bands, scoped to your selection.',
    formats: ['Word', 'PDF'],
  },
  {
    id: 'trend',
    icon: ArticleIcon,
    title: 'Trend & Briefing',
    description: 'REPS performance plus a written briefing with quadrant commentary and a recommendation.',
    formats: ['Word', 'PDF'],
  },
  {
    id: 'narrative',
    icon: AutoAwesomeIcon,
    title: 'Executive Briefing',
    description: 'Full executive briefing for the time period — performance, action log, and recommendations.',
    formats: ['Word', 'PDF'],
  },
  {
    id: 'rawdata',
    icon: DescriptionIcon,
    title: 'Raw Data Export',
    description: 'Scores, the OE Condition & REF Action Log, and case-study benchmarks for the time period.',
    formats: ['CSV', 'PDF'],
  },
];

// Each template maps to which report sections to include.
const TPL_SECTIONS = {
  comparison: { performance: true, narrative: false, trls: false, caseStudies: false },
  trend: { performance: true, narrative: true, trls: false, caseStudies: false },
  narrative: { performance: true, narrative: true, trls: true, caseStudies: false },
  rawdata: { performance: true, narrative: false, trls: true, caseStudies: true },
};

const TIME_PERIODS = [
  { value: 'last7', label: 'Last 7 days' },
  { value: 'last30', label: 'Last 30 days' },
  { value: 'last90', label: 'Last 90 days (quarter)' },
  { value: 'last365', label: 'Last 12 months' },
];

const FMT = { PDF: 'pdf', CSV: 'csv', Word: 'doc' };

/**
 * Reports on Demand — Mindmap branch 3, "Outputs → Reports on Demand".
 * Now powered by the real report engine (shared with the Reports builder):
 * generating actually produces a downloadable PDF / Word / CSV file.
 */
function ReportsOnDemand() {
  const { persona } = useDemoRole();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organizations } = useUserOrganizations(user?.uid);
  const { comparisons } = useSiteComparisons(organizations[0]?.id || null);
  const { queryScope } = useDemoRole();
  const scopeOptions = buildScopeOptions(comparisons, queryScope);
  const [period, setPeriod] = useState('last30');
  const [scope, setScope] = useState('enterprise');
  // Scoped members can't report on 'enterprise' — snap to their first allowed scope.
  useEffect(() => {
    if (scopeOptions.length && !scopeOptions.some((o) => o.value === scope)) {
      setScope(scopeOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(scopeOptions.map((o) => o.value))]);
  const [busy, setBusy] = useState(null); // `${tplId}:${format}`
  const [error, setError] = useState(null);

  const runExport = async (tpl, format) => {
    setBusy(`${tpl.id}:${format}`);
    setError(null);
    try {
      const model = await assembleReport({
        sel: TPL_SECTIONS[tpl.id],
        scopeValue: scope,
        period,
        comparisons,
        organizationId: organizations[0]?.id || null,
        thresholds: organizations[0]?.thresholds,
        title: tpl.title,
        personaLabel: `Prepared as ${persona.label}`,
      });
      const fmt = FMT[format];
      if (fmt === 'pdf') exportReportPDF(model);
      else if (fmt === 'csv') exportReportCSV(model);
      else exportReportDoc(model);
    } catch (e) {
      console.error('Report export failed:', e);
      setError(e.message || 'Could not generate this report.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1.5, flexWrap: 'wrap' }}>
        <ArticleIcon sx={{ color: 'primary.main', fontSize: 32 }} />
        <Typography variant="h4">Reports on Demand</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <OrgSwitcher />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Pick a time period and scope, then generate the report you need — each button produces a real, downloadable file.
        For full control (custom sections, saved templates), use the
      </Typography>
      <Button size="small" startIcon={<OpenInNewIcon />} sx={{ mb: 2 }} onClick={() => navigate('/drew/reports')}>
        Open the full Report Builder
      </Button>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="overline" color="text.secondary">Report parameters</Typography>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={5}>
              <TextField select fullWidth size="small" label="Time period" value={period} onChange={(e) => setPeriod(e.target.value)}>
                {TIME_PERIODS.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField select fullWidth size="small" label="Scope" value={scope} onChange={(e) => setScope(e.target.value)}>
                {scopeOptions.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <Chip label={`Viewing as: ${persona.label}`} size="small" variant="outlined" />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Grid container spacing={3}>
        {REPORT_TEMPLATES.map((tpl) => {
          const Icon = tpl.icon;
          return (
            <Grid item xs={12} md={6} key={tpl.id}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Icon sx={{ color: 'primary.main' }} />
                    <Typography variant="h6" fontWeight={700}>{tpl.title}</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 56 }}>
                    {tpl.description}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Stack direction="row" spacing={1}>
                    {tpl.formats.map((f) => {
                      const isBusy = busy === `${tpl.id}:${f}`;
                      return (
                        <Button
                          key={f}
                          variant={f === 'Word' ? 'contained' : 'outlined'}
                          size="small"
                          disabled={!!busy}
                          startIcon={isBusy ? <CircularProgress size={14} color="inherit" /> : (f === 'PDF' ? <PictureAsPdfIcon /> : f === 'Word' ? <ArticleIcon /> : <TableChartIcon />)}
                          onClick={() => runExport(tpl, f)}
                        >
                          {isBusy ? 'Generating…' : f}
                        </Button>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}

export default ReportsOnDemand;
